import apiClient from '@/services/api_client';
import { isValidRole } from '@/services/roles';
import { DataSource, IndexingStatus, Item, Role } from '@/types';

export type GetDatasourceDocumentsParams = {
    dataSourceIds: number[];
    tenantId: string;
    offset: number;
    pageSize: number;
    extensions?: string[];
    accessToken?: string;
    nameContains?: string;
    indexingStatuses?: IndexingStatus[];
};

export type GetChatsessionDocumentsParams = {
    tenantId: string;
    page: number;
    pageSize: number;
    extensions?: string[];
    chatSessionId?: string;
};

type GetAttachedFilesDatasource = {
    chatSessionId?: string;
    tenantId: string;
};

export type GetDatasourcesReponse = {
    items: Item[];
    count: number;
    nextOffset: number | null;
};

export type GetChatSessionDocumentsResponse = {
    items: Item[];
    count: number;
    active_items: Item[];
};

export type SearchItemsResponse = {
    items: Item[];
    totalMatches: number;
};

export interface DataSourcePermission {
    user_id: string;
    role: Role;
    name: string | null;
    email: string | null;
}

export interface DataSourcePermissionsResponse {
    permissions: DataSourcePermission[];
}

export interface DataSourceGroupPermission {
    group_id: number;
    role: Role;
    name: string | null;
    description: string | null;
    member_count: number;
    is_azure_group: boolean;
}

export interface DataSourceGroupPermissionsResponse {
    permissions: DataSourceGroupPermission[];
}

export interface TaskRun {
    id: string;
    result: string | null;
    error: string | null;
}

export async function getDataSources(
    tenantId: string,
    extensionFilter?: string[]
): Promise<DataSource[]> {
    const params = new URLSearchParams();
    params.set('tenant_id', tenantId);

    if (extensionFilter) {
        extensionFilter.forEach((ext) =>
            params.append('extension_filter', ext)
        );
    }

    const url = `/datasources?${params.toString()}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function getPptTemplateDatasource(
    tenantId: string
): Promise<DataSource> {
    const url = `/datasources/template?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function getAttachedFilesDatasource({
    chatSessionId,
    tenantId
}: GetAttachedFilesDatasource) {
    const url: string = `/datasource/${chatSessionId}?tenant_id=${tenantId}`;

    const response = await apiClient.get(url);
    return await response.json();
}

export async function getSelectedDataSources(
    tenantId: string,
    sessionId: string
): Promise<DataSource[]> {
    const url = `/datasources/multi-selected?tenant_id=${tenantId}&chat_session_id=${sessionId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function selectDataSources(
    sessionId: string,
    dataSources: DataSource[],
    selectDataSourceNames: string[],
    tenantId: string
) {
    const selectedDataSourceIds = dataSources
        .filter((dataSource) => selectDataSourceNames.includes(dataSource.name))
        .map((dataSource) => dataSource.id);
    const url = '/datasources/multi-selected';
    await apiClient.post(url, {
        data_source_ids: selectedDataSourceIds,
        tenant_id: tenantId,
        chat_session_id: sessionId
    });
}

export async function getChatSessionActiveItems(
    chatSessionId: string,
    tenantId: string
) {
    const url = `/chat_session/${chatSessionId}/active_items?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function setChatSessionActiveItems(
    chatSessionId: string,
    tenantId: string,
    dataSourceItemIds: string[]
) {
    const url = `/chat_session/${chatSessionId}/active_items`;
    const body = {
        tenant_id: tenantId,
        chat_session_id: chatSessionId,
        data_source_item_ids: dataSourceItemIds
    };
    return await apiClient.post(url, body);
}

export async function addChatSessionActiveItem(
    chatSessionId: string,
    tenantId: string,
    dataSourceItemId: string
) {
    const url = `/chat_session/${chatSessionId}/activate_item`;
    const body = {
        tenant_id: tenantId,
        chat_session_id: chatSessionId,
        data_source_item_id: dataSourceItemId
    };
    return await apiClient.post(url, body);
}

export async function removeChatSessionActiveItem(
    chatSessionId: string,
    tenantId: string,
    dataSourceItemId: string
) {
    const url = `/chat_session/${chatSessionId}/deactivate_item`;
    const body = {
        tenant_id: tenantId,
        chat_session_id: chatSessionId,
        data_source_item_id: dataSourceItemId
    };
    return await apiClient.post(url, body);
}

export async function reIndexDatasourceErrorFiles(
    dataSourceId: number,
    tenantId: string
) {
    const url = '/retry_index';
    const body = {
        data_source_id: dataSourceId,
        tenant_id: tenantId
    };
    await apiClient.post(url, body);
}

export async function syncSharepointDocuments(
    dataSourceId: number,
    tenantId: string
) {
    const url = '/sharepoint/sync';
    const body = {
        data_source_id: dataSourceId,
        tenant_id: tenantId
    };

    const response = await apiClient.post(url, body);
    return response.task_id ?? null;
}

export async function createDataSource(
    name: string,
    tenantId: string,
    visibility: string,
    description?: string,
    isTemplate?: boolean,
    ragType?: string
): Promise<DataSource> {
    const url = '/datasources';
    return await apiClient.post(url, {
        name,
        tenant_id: tenantId,
        visibility: visibility,
        description: description,
        isTemplate: isTemplate,
        rag_type: ragType
    });
}

export async function updateDataSource(
    dataSourceName: string,
    dataSourceId: number,
    tenantId: string,
    visibility?: string,
    description?: string,
    alias?: string
) {
    const url = '/datasources/update';
    const response = await apiClient.post(url, {
        name: dataSourceName,
        data_source_id: dataSourceId,
        tenant_id: tenantId,
        visibility: visibility,
        description: description,
        alias: alias
    });
    return response;
}

export async function deleteDataSource(
    dataSourceName: string,
    dataSourceId: number,
    tenantId: string
) {
    const url = '/datasources';
    await apiClient.delete(url, {
        name: dataSourceName,
        data_source_id: dataSourceId,
        tenant_id: tenantId
    });
}

export async function getDataSourceById(
    dataSourceId: string,
    tenantId: string
): Promise<DataSource> {
    const url = `/datasources/${dataSourceId}?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export interface LastSyncIndex {
    finished_at: string;
    added_count: number;
    updated_count: number;
    deleted_count: number;
    skipped_count: number;
    error_count: number;
}

export async function getLastSyncIndex(
    dataSourceId: string,
    tenantId: string
): Promise<LastSyncIndex | null> {
    const url = `/last_sync_index?tenant_id=${tenantId}&data_source_id=${dataSourceId}`;
    try {
        const response = await apiClient.get(url);
        return await response.json();
    } catch (error: unknown) {
        if (
            typeof error === 'object' &&
            error !== null &&
            'status' in error &&
            (error as { status: number }).status === 404
        ) {
            return null;
        }
        throw error;
    }
}

export async function searchDatasourcesItemsByQuery(
    tenantId: string,
    dataSourceIds: number[],
    searchQuery: string,
    signal?: AbortSignal,
    limit?: number,
    accessToken?: string
): Promise<SearchItemsResponse> {
    const params = new URLSearchParams();
    params.set('tenant_id', tenantId);
    for (const id of dataSourceIds) {
        params.append('data_source_ids', String(id));
    }
    params.set('search_query', searchQuery);
    if (limit !== undefined) {
        params.set('limit', limit.toString());
    }
    const url = `/datasources/items/search?${params.toString()}`;
    const extraHeaders =
        accessToken !== undefined
            ? { 'X-Microsoft-Access-Token': accessToken }
            : undefined;
    const response = await apiClient.get(url, { signal, extraHeaders });
    return await response.json();
}

export async function searchDataSourcesByItemsQuery(
    tenantId: string,
    searchQuery: string,
    signal?: AbortSignal
): Promise<DataSource[]> {
    const params = new URLSearchParams();
    params.set('tenant_id', tenantId);
    params.set('search_query', searchQuery);
    const url = `/datasources/search-by-items?${params.toString()}`;
    const response = await apiClient.get(url, { signal });
    return await response.json();
}

export async function getDatasourceDocuments({
    dataSourceIds,
    tenantId,
    offset,
    pageSize,
    extensions,
    accessToken,
    nameContains,
    indexingStatuses
}: GetDatasourceDocumentsParams): Promise<GetDatasourcesReponse> {
    const url = '/datasources/get_documents';

    const body = {
        tenant_id: tenantId,
        data_source_ids: dataSourceIds,
        offset: offset,
        size: pageSize,
        extensions: extensions || [],
        access_token: accessToken || null,
        name_contains: nameContains || null,
        indexing_statuses: indexingStatuses || []
    };

    return await apiClient.post(url, body);
}

export async function getDatasourceDocumentsRevalidated(
    {
        dataSourceIds,
        tenantId,
        offset,
        pageSize,
        extensions,
        accessToken
    }: GetDatasourceDocumentsParams,
    { etag }: { etag?: string } = {}
): Promise<{
    items: Item[] | null;
    count: number | null;
    nextOffset: number | null;
    notModified: boolean;
    etag?: string;
}> {
    const url = '/datasources/get_documents';

    const body = {
        tenant_id: tenantId,
        data_source_ids: dataSourceIds,
        offset: offset,
        size: pageSize,
        extensions: extensions || [],
        access_token: accessToken || null,
        etag: etag || null
    };

    try {
        const data = await apiClient.post(url, body);

        // If we get a successful response, it means data was modified
        return {
            items: data.items,
            count: data.count,
            nextOffset: data.nextOffset,
            notModified: false,
            etag: data.etag
        };
    } catch (error: unknown) {
        // Check if it's a 304 Not Modified response
        if (
            error &&
            typeof error === 'object' &&
            'status' in error &&
            error.status === 304
        ) {
            return {
                items: null,
                count: null,
                nextOffset: null,
                notModified: true,
                etag: etag
            };
        }
        throw error;
    }
}

export async function getChatSessionDocuments({
    tenantId,
    page,
    pageSize,
    extensions,
    chatSessionId
}: GetChatsessionDocumentsParams): Promise<GetChatSessionDocumentsResponse> {
    const params = new URLSearchParams();
    params.append('tenant_id', tenantId);
    params.set('page', String(page));
    params.set('size', String(pageSize));

    // Only add extensions parameter if array has elements
    if (extensions && extensions.length > 0) {
        extensions.forEach((ext) => params.append('extensions', ext));
    }

    const url: string = `/chat_session/${chatSessionId}/get_documents?${params.toString()}`;

    const response = await apiClient.get(url);
    return await response.json();
}

export async function getUserDataSourceRole(
    dataSourceId: string,
    tenantId: string
) {
    const url = `/datasources/${dataSourceId}/role?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    const role = await response.json();

    if (!isValidRole(role)) {
        throw new Error('Invalid role');
    }

    return role;
}

export async function getDataSourcePermissions(
    dataSourceId: string,
    tenantId: string
): Promise<DataSourcePermissionsResponse> {
    const url = `/datasources/${dataSourceId}/permissions?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function addUserPermissionToDataSource(
    dataSourceId: string,
    tenantId: string,
    userEmail: string,
    role: Role
): Promise<DataSourcePermission> {
    const url = `/datasources/${dataSourceId}/permissions/add`;
    const data = await apiClient.post(url, {
        tenant_id: tenantId,
        user_email: userEmail,
        role: role
    });
    return data;
}

export async function removeUserPermissionFromDataSource(
    dataSourceId: string,
    tenantId: string,
    userId: string
): Promise<void> {
    const url = `/datasources/${dataSourceId}/permissions/${userId}`;
    await apiClient.delete(url, {
        tenant_id: tenantId,
        user_id: userId
    });
}

export async function deleteDataSourceItem(
    datasourceItemId: string,
    tenantId: string
) {
    const url = `/datasources/items/${datasourceItemId}`;
    await apiClient.delete(url, {
        tenant_id: tenantId
    });
}

export async function deleteDataSourceItems(
    datasourceItemIds: string[],
    tenantId: string
) {
    const url = '/datasources/items';
    await apiClient.delete(url, {
        tenant_id: tenantId,
        datasource_item_ids: datasourceItemIds
    });
}

export async function getDataSourceGroupPermissions(
    dataSourceId: string,
    tenantId: string
): Promise<DataSourceGroupPermissionsResponse> {
    const url = `/datasources/${dataSourceId}/group_permissions?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function addGroupPermissionToDataSource(
    dataSourceId: string,
    tenantId: string,
    groupId: number,
    role: Role
): Promise<DataSourceGroupPermission> {
    const url = `/datasources/${dataSourceId}/group_permissions/add`;
    const data = await apiClient.post(url, {
        tenant_id: tenantId,
        group_id: groupId,
        role: role
    });
    return data;
}

export async function removeGroupPermissionFromDataSource(
    dataSourceId: string,
    tenantId: string,
    groupId: number
): Promise<void> {
    const url = `/datasources/${dataSourceId}/group_permissions/${groupId}`;
    await apiClient.delete(url, {
        tenant_id: tenantId,
        group_id: groupId
    });
}

export async function getIndexTaskRunsByDatasource(
    dataSourceId: number,
    tenantId: string
): Promise<TaskRun[]> {
    const url = `/datasources/${dataSourceId}/index_task_runs`;
    return await apiClient.post(url, {
        tenant_id: tenantId,
        data_source_id: dataSourceId
    });
}
